/**
 * Ring decimation for the yield visualisation — Douglas–Peucker at 0,5 mm.
 *
 * A traced outline carries a point every millimetre or two because the tracer
 * follows pixels; the yield SVG redraws every placed instance of it and does
 * not need that density. This module thins a ring so that NO original vertex
 * ends up further than the tolerance from the thinned outline — the classic
 * Douglas–Peucker guarantee — which at 0,5 mm is far below anything a strip
 * drawn a few hundred pixels wide can show.
 *
 * It is a DRAWING aid, never an assessment input: everything the placer and
 * the lattice search measure keeps running on the full ring. The decimated
 * ring exists only to travel to the UI and be stroked.
 *
 * Zero imports, like the rest of this library: the same file compiles in the
 * Worker project, the SPA project and Node tooling.
 *
 * Rings are closed implicitly (the last point joins back to the first, do not
 * repeat it), same convention as `geometry.ts`. Douglas–Peucker is defined on
 * an open chain with fixed endpoints, so a ring is split at two anchors —
 * the first vertex and the vertex farthest from it — and each half is thinned
 * on its own. Both anchors always survive, the output is a subsequence of the
 * input, and a ring never thins below three vertices.
 */

/** Default tolerance, mm. The yield viz decimates at this. */
export const DECIMATE_TOLERANCE_MM = 0.5

/**
 * Thin a closed ring. Every dropped vertex lies within `toleranceMm` of the
 * segment between the two kept vertices around it, so the thinned ring stays
 * within the tolerance of the original everywhere. Returns fresh arrays; the
 * input is never touched.
 */
export function decimateRing(
  ring: readonly (readonly [number, number])[],
  toleranceMm: number = DECIMATE_TOLERANCE_MM,
): [number, number][] {
  if (!(toleranceMm >= 0)) throw new Error('decimateRing: toleranceMm must be >= 0')
  const n = ring.length
  if (n <= 3) return ring.map((p) => [p[0], p[1]])

  // Second anchor: the vertex farthest from the first. Splitting at the two
  // most distant points keeps both chains well-conditioned; splitting at an
  // arbitrary index can put a whole lobe of the ring on one chord.
  let far = 1
  let farDist = -1
  const p0 = ring[0] as readonly [number, number]
  for (let i = 1; i < n; i++) {
    const p = ring[i] as readonly [number, number]
    const d = (p[0] - p0[0]) * (p[0] - p0[0]) + (p[1] - p0[1]) * (p[1] - p0[1])
    if (d > farDist) {
      farDist = d
      far = i
    }
  }

  const keep = new Uint8Array(n)
  keep[0] = 1
  keep[far] = 1
  thinChain(ring, 0, far, toleranceMm, keep)
  thinChainWrapped(ring, far, n, toleranceMm, keep)

  const out: [number, number][] = []
  for (let i = 0; i < n; i++) {
    if (keep[i] === 1) {
      const p = ring[i] as readonly [number, number]
      out.push([p[0], p[1]])
    }
  }

  // A sliver thinner than the tolerance can collapse to its two anchors. Keep
  // the most deviant remaining vertex so the result is still a ring — three
  // points, implicitly closed — rather than a bare segment.
  if (out.length === 2) {
    let bestAt = -1
    let bestDist = -1
    const a = ring[0] as readonly [number, number]
    const b = ring[far] as readonly [number, number]
    for (let i = 1; i < n; i++) {
      if (i === far) continue
      const p = ring[i] as readonly [number, number]
      const d = segmentDistance(p, a, b)
      if (d > bestDist) {
        bestDist = d
        bestAt = i
      }
    }
    if (bestAt < 0) return ring.slice(0, 3).map((p) => [p[0], p[1]])
    const extra = ring[bestAt] as readonly [number, number]
    const insertAt = bestAt < far ? 1 : 2
    out.splice(insertAt, 0, [extra[0], extra[1]])
  }

  return out
}

/**
 * Douglas–Peucker over `ring[from..to]` (both anchors already kept), marking
 * survivors in `keep`. Iterative with an explicit stack: a pathological chain
 * recurses to its own length, and a stack costs nothing.
 */
function thinChain(
  ring: readonly (readonly [number, number])[],
  from: number,
  to: number,
  toleranceMm: number,
  keep: Uint8Array,
): void {
  const stack: number[] = [from, to]
  while (stack.length > 0) {
    const hi = stack.pop() as number
    const lo = stack.pop() as number
    if (hi - lo < 2) continue
    const a = ring[lo] as readonly [number, number]
    const b = ring[hi] as readonly [number, number]
    let worstAt = -1
    let worst = toleranceMm
    for (let i = lo + 1; i < hi; i++) {
      const d = segmentDistance(ring[i] as readonly [number, number], a, b)
      if (d > worst) {
        worst = d
        worstAt = i
      }
    }
    if (worstAt < 0) continue
    keep[worstAt] = 1
    stack.push(lo, worstAt, worstAt, hi)
  }
}

/**
 * The second half of the ring: `ring[far] … ring[n-1] … ring[0]`, i.e. the
 * chain that wraps through the implicit closing segment. Rather than copying
 * the points into a shifted array, indices ≥ `n` read modulo `n` — `keep`
 * marks land on the true index either way.
 */
function thinChainWrapped(
  ring: readonly (readonly [number, number])[],
  far: number,
  n: number,
  toleranceMm: number,
  keep: Uint8Array,
): void {
  const at = (i: number): readonly [number, number] =>
    ring[i < n ? i : i - n] as readonly [number, number]
  const stack: number[] = [far, n]
  while (stack.length > 0) {
    const hi = stack.pop() as number
    const lo = stack.pop() as number
    if (hi - lo < 2) continue
    const a = at(lo)
    const b = at(hi)
    let worstAt = -1
    let worst = toleranceMm
    for (let i = lo + 1; i < hi; i++) {
      const d = segmentDistance(at(i), a, b)
      if (d > worst) {
        worst = d
        worstAt = i
      }
    }
    if (worstAt < 0) continue
    keep[worstAt < n ? worstAt : worstAt - n] = 1
    stack.push(lo, worstAt, worstAt, hi)
  }
}

/** Distance from `p` to the SEGMENT `a–b` (not the infinite line). */
function segmentDistance(
  p: readonly [number, number],
  a: readonly [number, number],
  b: readonly [number, number],
): number {
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  const len2 = dx * dx + dy * dy
  if (len2 === 0) return Math.hypot(p[0] - a[0], p[1] - a[1])
  let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2
  if (t < 0) t = 0
  else if (t > 1) t = 1
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy))
}
